import type { zh } from "./zh";

/** English UI copy. Must mirror `zh` key-for-key (checked at compile time). */
export const en = {
	app: {
		title: "Muhaha · Games & dramas",
	},
	meta: {
		pageTitle: "{{page}} · Muhaha",
		searchTitle: "Search",
		searchDescription: "Search Muhaha for games, dramas, and creators.",
		playableDescription: "Play community mini-games and watch interactive dramas on Muhaha.",
		profileDescription: "Games and dramas published by {{name}} on Muhaha.",
		topicDescription: "Games and dramas tagged #{{topic}} on Muhaha.",
		termsDescription: "The terms you agree to when you use Muhaha.",
		privacyDescription: "How Muhaha collects, uses, and protects your data.",
	},
	notFound: {
		title: "Page not found",
		description: "This link is broken, or the content it pointed to is gone.",
		backHome: "Back to Discover",
	},
	common: {
		back: "Back",
		loading: "Loading…",
		loadFailed: "Failed to load",
		loadMore: "Load more",
		loadMoreFailed: "Failed to load more",
		refresh: "Refresh",
		retry: "Retry",
		cancel: "Cancel",
		playAd: "Play ad",
		cancelAd: "Cancel ad",
		loadingAd: "Loading ad",
		advertisement: "Advertisement",
		remove: "Remove",
		confirm: "Confirm",
		close: "Close",
		send: "Send",
		signIn: "Sign in",
		signInUp: "Sign in / Sign up",
		justNow: "just now",
	},
	datePicker: {
		placeholder: "Select a date",
		clear: "Clear date",
	},
	layout: {
		search: "Search Muhaha",
		home: "Muhaha home",
		about: "About",
		playOnMobile: "Play on mobile",
		notifications: "Notifications",
		myProfile: "My profile",
		accountMenu: "Account menu",
		community: "Community",
		lightMode: "Light mode",
		darkMode: "Dark mode",
		settings: "Settings",
		signOut: "Sign out",
		guestMode: "Guest mode",
		popularTopics: "Popular Topics",
		allTopics: "All topics",
		noTopics: "No popular topics yet",
	},
	mobilePlay: {
		getTheApp: "Get the App",
		scanToPlay: "Scan to play on your phone",
		title: "Play on mobile",
		description: "Scan the QR code with your phone to download the Muhaha app.",
		downloadTitle: "Download the Muhaha app",
		downloadDescription: "Choose your app store to download Muhaha for iOS or Android.",
		storeDownloadDescription: "Scan with your phone or choose an app store to download Muhaha.",
		redirectingDescription:
			"Taking you to the app store for your device. If nothing happens, choose a store below.",
		iosStore: "iOS · App Store",
		androidStore: "Android · Google Play",
		iosDownload: "Download on iOS",
		androidDownload: "Download on Android",
		downloadQrAlt: "QR code to download the Muhaha app",
		showQr: "Show QR",
		copyLink: "Copy link",
		linkCopied: "Link copied",
		copyFailed: "Couldn't copy the link",
		qrAlt: "QR code to open Muhaha on mobile",
	},
	about: {
		eyebrow: "About Muhaha",
		title: "Mini games and interactive dramas",
		description:
			"Muhaha is a creative platform for endless mini playables, from games to interactive dramas. Play, create, and make anything your own on web and mobile.",
		mobileTitleLead: "Discover",
		mobileTitleAccent: "Endless Fun.",
		mobileDownloadCta: "Scan to play Muhaha on your phone",
		createTitle: "Create Anything",
		createText: "Turn an idea into a playable game or a branching drama.",
		playTitle: "Play Together",
		playText: "Explore community-made worlds and share the moment with friends.",
		remixTitle: "Remix Fast",
		remixText: "Borrow a structure, change the vibe, and publish your own version.",
		tagline: "Create. Play. Remix.",
	},
	footer: {
		copyright: "Copyright © 2026 Yahaha Studios. All rights reserved.",
		navigation: "Footer",
		support: "Support",
		privacy: "Privacy Policy",
		terms: "Terms of Service",
	},
	nav: {
		discover: "Discover",
		create: "Create",
		library: "Library",
		friends: "Friends",
		messages: "Messages",
		wallet: "Credits",
		me: "Me",
	},
	contentType: {
		all: "All",
		game: "Games",
		drama: "Dramas",
	},
	card: {
		play: "Play",
		watch: "Watch",
		like: "Like",
	},
	auth: {
		modalTitle: "Sign in to Muhaha",
		signInTitle: "Sign in",
		signUpTitle: "Create your account",
		newToMuhaha: "New to Muhaha?",
		haveAccount: "Already have an account?",
		signUp: "Sign up",
		heroTitle: "Enter your interactive universe",
		heroSubtitle: "Sign in with an email code to unlock the full experience",
		emailLabel: "Email address",
		changeEmail: "Use another email",
		codeSentTo: "Code sent to",
		codePlaceholder: "6-digit code",
		resendIn: "Resend in {{s}}s",
		resend: "Resend code",
		getCode: "Get code",
		continueWithEmail: "Continue with email",
		verifyAndContinue: "Verify and continue",
		signIn: "Sign in",
		or: "OR",
		googleContinue: "Continue with Google",
		appleContinue: "Continue with Apple",
		appleNoToken: "Apple did not return an identity token",
		guestContinue: "Try it first (guest)",
		agreeTos: "By signing in you agree to Muhaha's Terms of Service and Privacy Policy",
		agreePrefix: "By continuing, you agree to Muhaha's",
		termsOfService: "Terms of Service",
		agreeJoiner: "and",
		privacyPolicy: "Privacy Policy",
		gateTitle: "Sign in to continue",
		gateDesc: "Sign in to Muhaha to chat, create, and collect.",
		sessionExpired: "Your session has expired",
		inviteBannerText: "You've been invited to Muhaha — invite code",
		inviteBannerCta: "Sign up",
		inviteCodeToggle: "Have an invite code?",
		inviteCodeLabel: "Invite code",
		inviteCodePlaceholder: "6-character code",
		inviteCodeApplied: "Signing up with invite code",
		inviteCodeChange: "Change",
		inviteCodeValid: "Looks good — it'll be sent with your signup",
		inviteCodeInvalid: "Invite codes are 6 characters, without I, O, 0 or 1",
		codeSent: "Verification code sent to your email",
		codeSendFailed: "Failed to send the code",
		signedIn: "Signed in",
		signedInWithInvite: "Signed in — invite code {{code}} was sent with your signup",
		codeInvalid: "Invalid verification code",
		googleFailed: "Google sign-in failed",
		appleFailed: "Apple sign-in failed",
		guestEntered: "You're in guest mode",
		guestFailed: "Failed to enter guest mode",
		signInFailed: "Sign-in failed",
		traveler: "Traveler",
	},
	login: {
		redirecting: "Redirecting…",
		authorizeFailed: "Couldn't complete sign-in. Please try again.",
	},
	loginSupport: {
		title: "Can't log in?",
		subtitle: "Follow these steps to get back into your Muhaha account.",
		emailSubject: "Muhaha login help",
		emailSignInMethod: "Sign-in method:",
		emailDevice: "Device and OS:",
		emailAppVersion: "App version:",
		emailProblem: "What happened:",
		sameMethodTitle: "Use the same sign-in method",
		sameMethodBody1:
			"If you already have a Muhaha account, choose the same method you used when you created it.",
		sameMethodBody2:
			'On the login screen, choose "Continue with Apple", "Continue with Google", or enter your email address to receive a verification code.',
		appleTitle: "Apple sign-in",
		appleBody1:
			"Make sure you are signed in to the intended Apple Account, then complete the Apple authorization window without closing it.",
		appleBody2:
			"If the authorization window was cancelled or closed, return to Muhaha and try the same button again.",
		googleTitle: "Google sign-in",
		googleBody1:
			"Make sure you are signed in to the intended Google account on your device, then complete the provider sign-in window without closing it.",
		googleBody2:
			"If the provider window was cancelled or closed, return to Muhaha and try the same button again.",
		emailCodeTitle: "Email verification code",
		emailCodeBody1:
			"Check that the email address is correct, then look for the Muhaha code in your inbox, spam, or junk folder.",
		emailCodeBody2:
			"If you requested more than one code, use the newest one. You can request a fresh code from the email login screen.",
		retryTitle: "Before you retry",
		retryBody1:
			"Check your internet connection, fully close and reopen Muhaha, and install the latest available app update before trying again.",
		retryBody2:
			"If authentication is being blocked, try another network and temporarily turn off any VPN or proxy.",
		helpTitle: "Still need help?",
		helpBody:
			"Email [{{email}}](<{{href}}>) and include your sign-in method, device and OS, app version, the approximate time of the problem, and a screenshot of the error.",
		securityBody: "For your security, never send us an email verification code.",
	},
	playable: {
		untitled: "Untitled",
		defaultTagline: "A work waiting to be opened",
		anonymousAuthor: "Anonymous creator",
		expired: "No longer available",
		expiresInMinutes: "Ends in {{count}} min",
		expiresInHours: "Ends in {{count}} h",
		expiresInDays: "Ends in {{count}} d",
		pinned: "Pinned",
		draft: "Draft",
		archived: "Unlisted from your profile",
		anonPublished: "Posted anonymously",
		unpublishedEdits: "Unpublished changes",
		visibilityFollowers: "Followers only",
		visibilityFriends: "Friends only",
		visibilityUnlisted: "Link only",
		visibilityPrivate: "Only me",
		visibilityDirected: "Sent to specific people",
	},
	media: {
		zoom: "Zoom",
		horizontal: "Horizontal",
		vertical: "Vertical",
		applyCrop: "Apply crop",
		cropFailed: "This image could not be read or cropped",
	},
		comments: {
			playableAttachment: "Playable remix",
			attachMedia: "Attach image or video",
			invalidMedia: "Choose an image or video up to 80 MB",
			reply: "Reply",
			replyPlaceholder: "Write a reply...",
			showReplies: "View {{count}} replies",
			hideReplies: "Hide replies",
			loadMoreReplies: "Load more replies",
			likeFailed: "Failed to update like",
			replyFailed: "Failed to send reply",
			repliesLoadFailed: "Failed to load replies",
		},
	createGame: {
		quick: "Quick",
		deep: "Deep",
		quickHint: "Quick generation",
		deepHint: "Deep generation",
		remixMode: "Remix mode",
		remixOf: "Remixing",
		remixOfHint: "Your prompt is applied on top of this game.",
		remixSourceUnavailable: "This game could not be loaded.",
		remixNotAllowed: "The author has not opened this game up for you to remix.",
		remixUnsupported: "This game doesn't support remix yet.",
		remixUntitled: "Untitled game",
		remixClear: "Start from scratch instead",
		promptPlaceholder:
			"Describe the mini game: mechanics, characters, mood, win condition, visual style...",
		generate: "Generate game",
		freeLeft: "First 3 generations free · {{count}} left",
		costHint: "Each generation uses Credits · Balance {{count}}",
		notEnoughCredits: "Not enough Credits to start generation.",
		startFailed: "Failed to start generation",
		attachImage: "Attach image",
		attachAudio: "Attach audio",
		powers: "Powers",
		hotPowers: "Hot Powers",
		selectionLimit: "Select up to {{count}} items",
		deepRemixUnavailable: "Remixes currently use Quick mode",
		deepAttachmentsUnavailable: "Deep planning does not support media attachments yet",
		remixDemos: "Remix inspiration",
		remixDemosHint: "Choose a showcase to start",
		before: "Before",
		after: "After",
		remixCount: "{{count}} remixes",
		attachmentsLabel: "Attached files",
		removeAttachment: "Remove attachment {{name}}",
		attachmentLimit: "You can attach up to {{count}} files",
		attachmentInvalidType: "This file type is not supported",
		attachmentTooLarge: "Each attachment must be 10 MB or smaller",
		attachmentUploadFailed: "Attachment upload failed. Try again.",
		powersComingSoon: "Powers is coming soon",
		openWallet: "Open wallet",
		handoffUnavailable: "Muhaha Create is unavailable, so creation cannot start.",
		redirectingToNow: "Opening Muhaha Create...",
		description:
			"After sending, you'll land in Muhaha Create — the canvas editor that builds your game and where you keep refining it.",
	},
	remix: {
		title: 'Remix "{{title}}"',
		untitled: "Untitled",
		promptTitle: "Got new ideas on this project? Remix it!",
		placeholder: "type your idea and start building...",
		send: "Start remix",
		hint: "We'll copy this into your drafts first, then generate a new version from that fork.",
		notEnoughCredits: "Not enough Credits to remix.",
		prepareFailed: "Failed to prepare remix. Please try again.",
	},
	discover: {
		expandSidebar: "Expand sidebar",
		pinSidebar: "Keep expanded",
		collapseSidebar: "Collapse sidebar",
		expandPlayNext: "Show You might love",
		collapsePlayNext: "Collapse You might love",

		followingDescription: "New games from the creators you follow.",
		followingEmptyTitle: "Find your next favorite creator",
		followingEmptyDescription: "No games here yet. Follow a few creators to make this feed your own.",
		creatorsToFollow: "People you might vibe with",
		creatorsToFollowDescription: "Meet the makers. Try their games. Stay for what’s next.",
		viewFollowing: "See their games",
		followCreator: "Follow {{name}}",
		unfollowCreator: "Unfollow {{name}}",
		creatorFollowers: "{{followers}} followers",
		visitCreator: "Visit creator",
		noCreatorSuggestions: "No creator suggestions right now. Explore games to discover someone new.",
		curatedStarterTitle: "Start with a curated pick",
		exploreStarterTitle: "Picks from Explore",
		starterDescription: "Pick a cover and jump in. Your next round is one click away.",
		noStarterSuggestions: "Explore the latest games to find your first favorite.",

		browseGames: "Browse games",
		recentlyPlayed: "Recently played",
		latest: "Latest",
		popular: "Popular",
		recentlyPlayedDescription: "Pick up where you left off. Your most recent games come first.",
		latestDescription: "Freshly published games, newest first.",
		popularDescription: "Discover the games trending right now.",
		historySignInTitle: "Your recently played games",
		historySignInDescription: "Sign in to see your play history and jump back into a favorite.",
		followingSignInTitle: "Keep up with your favorites",
		followingSignInDescription: "Sign in to see games from the creators you follow.",
		historyEmptyTitle: "Your next favorite starts here",
		historyEmptyDescription: "Play a game and it will appear here, ready for another round.",
		heroFeatured: "Featured",
		autoPreviews: "Automatic game previews",
		pausePreviews: "Pause previews",
		resumePreviews: "Enable previews",
		sortGames: "Sort games",
		sortFeatured: "Featured",
		sortHot: "Hot",
		sortLatest: "Latest",
		allTags: "All tags",
		allTagsDescription: "Find your next favorite by genre, mood, or the way you like to play.",
		findTag: "Filter tags…",
		noTags: "No matching tags",
		randomGame: "Random game",
		backToTop: "Back to top",
		randomFailed: "Could not find a game. Please try again.",
		playNext: "You might love",
		noSimilarGames: "More games are on the way.",
		collections: "Collections",
		playCount: "{{value}} plays",
		createdBy: "Created by",
		explore: "Explore",
		exploreDescription: "A little curiosity. A new favorite.",
		browseDescription: "Pick something and jump right in.",
		categories: "Browse categories",
		categoriesFailed: "Categories are temporarily unavailable.",
		seeAll: "See all",
		backToExplore: "Back to Explore",
		previousIn: "Previous items in {{title}}",
		nextIn: "Next items in {{title}}",
		game: "Game",
		drama: "Interactive story",

		forYou: "For You",
		trending: "Trending",
		following: "Following",
		signInTitle: "Sign in to start exploring",
		signInDesc: "Sign in to browse community games and interactive dramas.",
		signInNow: "Sign in now",
		loadingContent: "Loading content…",
		emptyTitle: "Nothing here yet",
		emptyDesc: "Community content is on its way — refresh again in a bit.",
		loadFailed: "Failed to load content",
		heroDesc:
			"Create playable mini games and interactive dramas with the community. Play instantly, remix what you love, publish the moment inspiration strikes.",
		heroCreate: "CREATE.",
		heroPlay: "PLAY.",
		heroRemix: "REMIX.",
		tileDramas: "Drama universe",
		tileStories: "Interactive stories",
		tileScripts: "Co-created scripts",
		tileRadio: "Inspiration radio",
	},
	friends: {
		eyebrow: "Friends",
		title: "Friends",
		signInTitle: "Sign in to see friends",
		signInDesc: "Add friends, handle friend requests, and pick friends to play with in playables.",
		pageDesc: "Manage friends and requests.",
		addFriend: "Add friend",
		friendsTab: "Friends",
		requestsTab: "Requests",
		newRequests: "New friend requests",
		noFriends: "No friends yet",
		noFriendsDesc: "Add friends by handle, or start friend interactions from a playable.",
		dmButton: "Message",
		removeButton: "Remove",
		removeConfirm: "Remove {{name}} from your friends?",
		removedNotice: "Friend removed",
		removeFailed: "Failed to remove friend",
		blockedTab: "Blocked",
		blockButton: "Block",
		blockConfirm: "Block {{name}}? They won't be able to message you or send friend requests, and you'll no longer be friends.",
		blockedNotice: "User blocked",
		blockFailed: "Failed to block the user",
		unblockButton: "Unblock",
		unblockedNotice: "User unblocked",
		unblockFailed: "Failed to unblock the user",
		noBlocked: "No blocked users",
		noBlockedDesc: "Blocked people can't message you or send you friend requests.",
		noRequests: "No pending requests",
		noRequestsDesc: "New friend requests will show up here.",
		wantsToAdd: "wants to be your friend",
		ignore: "Ignore",
		accept: "Accept",
		addModalDesc: "Enter their handle.",
		handleLabel: "Handle",
		sendRequest: "Send request",
		loadFailed: "Failed to load friends",
		handleRequired: "Please enter a handle",
		nowFriends: "You're now friends",
		requestSent: "Friend request sent",
		requestFailed: "Failed to send the friend request",
		acceptedNotice: "Friend request accepted",
		acceptFailed: "Failed to accept the request",
		declinedNotice: "Friend request ignored",
		declineFailed: "Failed to handle the request",
		bridgeRequestDesc: "This playable wants to send a friend request to this user.",
		pickTitle: "Pick friends",
		pickedCount: "Selected {{picked}}/{{limit}}",
		noFriendsYet: "No friends yet.",
		goAdd: "Add some",
		unknownUser: "Unknown user",
		user: "User",
	},
	dm: {
		title: "Messages",
		signInTitle: "Sign in to see messages",
		signInDesc: "Direct-message accepted friends via Stream Chat.",
		pageDesc: "Powered by Stream Chat — friends only.",
		empty: "No messages yet",
		emptyDesc: "Pick a friend on the Friends page to start chatting.",
		goFriends: "Go to Friends",
		noMessagesYet: "No messages yet",
		sayHi: "No messages yet — say hi!",
		inputPlaceholder: "Type a message…",
		resizeWindow: "Resize chat window",
		loadFailed: "Failed to load messages",
		openFailed: "Couldn't open the conversation",
		messagesLoadFailed: "Failed to load messages",
		messageReactions: "Message reactions",
		sendFailed: "Failed to send the message",
		friendRequired: "Become friends to chat",
		friendRequiredDesc: "Send a friend request and wait for it to be accepted.",
		offline: "You're offline. Messages will reconnect automatically.",
		sharedPlayableBy: "by",
		sharedPlayablePlays: "{{count}} plays",
		playTogether: "Play together",
		liveRoom: "Live room · {{count}} inside",
		roomEnded: "This private room is no longer available",
		joinRoom: "Join room",
		roomUnavailable: "Room unavailable",
	},
	library: {
		title: "Library",
		subtitle: "Your favorites and browsing history",
		favorites: "Favorites",
		history: "History",
		noFavorites: "No favorites yet",
		noHistory: "No history yet",
		emptyDesc: "Browse the Discover page and favorite the work you love.",
	},
	notifications: {
		title: "Notifications",
		subtitle: "Who's watching your world",
		read: "Read",
		empty: "No notifications",
		emptyDesc: "New interactions will show up here.",
		loadFailed: "Failed to load notifications",
		interacted: "interacted with you",
		sections: {
			likes: "Likes & Saves",
			likesDesc: "See who liked or saved your content",
			following: "Following",
			followingDesc: "See new followers and quiet admirers",
			myPlayables: "My Playables",
			myPlayablesDesc: "Creations, generation status, and in-playable activity",
			subscriptions: "Subscriptions",
			subscriptionsDesc: "Updates from followed creators and saved playables",
			earlier: "Recent activity",
			showAll: "All activity",
		},
		labels: {
			follow: "followed you",
			comment: "commented on your work",
			reply: "replied to you",
			likePlayable: "liked your work",
			likeComment: "liked your comment",
			friendRequest: "wants to be your friend",
			friendAccept: "accepted your friend request",
			friendMatch: "became your friend",
			directMessage: "sent you a message",
			admirer: "is quietly following you",
			levelUp: "leveled up",
			playableMessage: "sent a message in a playable",
			generationStarted: "'s generation started",
			generationReady: "'s generation is ready",
			generationFailed: "'s generation failed",
			creatorBroadcast: "posted a creator broadcast",
			dramaUpdate: "updated a drama",
			playableNew: "published a new work",
		},
	},
	messages: {
		title: "Messages",
		markAllRead: "Mark all read",
		subtitle: "Conversations, interactions, and follower activity in one place",
		interactions: "Interactions",
		newFollowers: "New followers",
		updates: "Updates",
		followBack: "Follow back",
		conversations: "Conversations",
		empty: "No conversations yet",
		emptyDesc: "Find someone you'd like to talk to and send the first message.",
		findPeople: "Find people",
		newGroup: "New group",
		groupName: "Group name (optional)",
		createGroup: "Create group",
		groupMembers: "{{count}} members",
		groupMembersTitle: "Group members",
		viewGroupMembers: "View group members",
		groupCreateFailed: "Failed to create the group",
		followedBack: "Following",
		followBackFailed: "Failed to follow back",
		addFriend: "Add friend",
		requested: "Requested",
		sayHi: "Say hi",
	},
	profile: {
		historyButton: "History",
		shareProfile: "Share profile",
		shareProfileTitle: "Share Profile",
		profileLink: "Profile link",
		copyProfileLink: "Copy link",
		accountOverview: "Account",
		viewCreditsHistory: "View Credits and history",
		communityLinks: "Community",
		loadFailed: "Failed to load the user",
		notFound: "This user wandered off",
		followers: "Followers",
		followingLabel: "Following",
		postsLabel: "Posts",
		playsLabel: "Plays",
		likesReceivedLabel: "Likes received",
		favoritesLabel: "Saves received",
		likesAndSaves: "Likes & Saves",
		likesAndSavesTitle: "Likes & Saves",
		totalWorksPlaysLabel: "All-time plays",
		followersTitle: "Followers",
		followingTitle: "Following",
		followListFailed: "Failed to load this list",
		noFollowUsers: "No users yet",
		editProfile: "Edit profile",
		myProjects: "My projects",
		addFriend: "Add friend",
		acceptFriend: "Accept friend",
		friendRequestSent: "Request sent",
		friendsStatus: "Friends",
		followingBtn: "Following",
		follow: "Follow",
		contentTabs: "Profile content",
		postsTab: "Posts",
		likesTab: "Likes",
		savedTab: "Saved",
		theirPosts: "Their posts",
		signInToViewTitle: "Sign in to visit this profile",
		signInToViewDesc: "Sign in to see this creator's published games and dramas.",
		noPublicWorks: "No published works yet",
		createFirstWorkDesc: "Start with an idea and create your first work.",
		notPublishedWorkDesc: "They haven't published any works yet.",
		createWork: "Start creating",
		published: "Published",
		editWork: "Edit work",
		editWorkFailed: "Failed to open Muhaha Create",
		deleteWork: "Delete work",
		deleteWorkTitle: "Delete work",
		deleteWorkConfirm: "Delete “{{title}}”?",
		deleteWorkHint:
			"The work and its interaction data will be permanently deleted. This cannot be undone.",
		deletingWork: "Deleting…",
		workDeleted: "Work deleted",
		deleteWorkFailed: "Failed to delete the work",
		workActions: "Actions for {{title}}",
		pinWork: "Pin to profile",
		unpinWork: "Unpin",
		workPinned: "Pinned to your profile",
		workUnpinned: "Unpinned",
		pinWorkFailed: "Failed to update the pin",
		removeLike: "Remove like",
		likeRemoved: "Removed from Likes",
		likeUpdateFailed: "Failed to update the like",
		removeFavorite: "Remove from favorites",
		shareFailed: "Failed to share the work",
		likesLoadFailed: "Failed to load liked works",
		noLikedWorks: "No liked works yet",
			noLikedWorksDesc: "Explore works that catch your interest.",
			youMayAlsoLike: "You may also like",
		message: "Message",
		broadcastTitle: "Broadcast to followers",
		broadcast: "Broadcast",
		broadcastPlaceholder: "Tell followers what's new in this work...",
		sendBroadcast: "Send broadcast",
		broadcastSent: "Broadcast sent to {{count}} people",
		broadcastFailed: "Failed to send the broadcast",
		followFailed: "Failed to update follow",
	},
	search: {
		signInToSearch: "Sign in to search games and creators",
		placeholder: "Search games, creators…",
		clear: "Clear search",
		promptTitle: "Search for something",
		promptDesc: "Find by title, tag, or creator name.",
		noResults: "No results found",
		tryAnother: 'Try a different keyword than "{{query}}"',
		creators: "Creators",
		playables: "Games",
	},
	ageGate: {
		title: "You can't use Muhaha yet",
		description:
			"You must be at least {{years}} years old to use Muhaha. This requirement is part of our Terms of Service.",
		mistakeHint:
			"Entered the wrong date? Correct your birthday below and your account unlocks right away.",
		birthdayLabel: "Date of birth",
		required: "Enter your date of birth",
		tooYoung: "You must be at least {{years}} years old. Check the year and try again.",
		save: "Save and unlock",
		saveFailed: "Couldn't update your birthday",
		unlocked: "Birthday updated — welcome back",
		signOut: "Sign out instead",
	},
	settings: {
		title: "Settings",
		subtitle: "Manage your profile and account",
		avatar: "Avatar",
		uploadAvatar: "Upload avatar",
		avatarUploading: "Uploading",
		avatarUploadHint: "Images up to 5 MB. Save your profile after uploading.",
		avatarUpdated: "Avatar updated",
		avatarUpdateFailed: "Failed to update avatar",
		avatarInvalidType: "Choose an image file",
		avatarTooLarge: "Avatar must be 5 MB or smaller",
		displayName: "Display name",
		handleLabel: "Username (handle)",
		handleHint: "Letters, numbers, and underscores only",
		bio: "Bio",
		save: "Save",
		signOut: "Sign out",
		accountType: "Account type: ",
		guest: "Guest",
		registered: "Registered",
		emailLabel: "Email: ",
		guestNote:
			"Guest data only lives on this device. Sign in with email to keep your work and favorites forever.",
		profileUpdated: "Profile updated",
		saveFailed: "Failed to save",
		language: "Language",
		gender: "Gender",
		preferNotToSay: "Prefer not to say",
		genderFemale: "Female",
		genderMale: "Male",
		genderNonBinary: "Non-binary",
		genderSelfDescribed: "Self-described",
		birthday: "Birthday",
		birthdayTooYoung: "Account holders must be at least {{years}} years old",
		cropAvatar: "Crop avatar",
		notificationPrefs: "Notification preferences",
		notificationPrefsFailed: "Failed to save notification preferences",
		notificationGroups: {
			social: "Social connections",
			engagement: "Likes and comments",
			creation: "Generation progress",
			updates: "Creator and work updates",
			messages: "Playable messages",
		},
		notificationGroupDescriptions: {
			social: "Follows, friend requests, and matches",
			engagement: "New interactions on works and comments",
			creation: "Generation started, completed, or failed",
			updates: "Broadcasts, new works, and drama updates",
			messages: "Messages sent from inside works",
		},
		accountAndPrivacy: "Account and privacy",
		cosmetics: "Avatars and cosmetics",
		downloadData: "Download my data",
		exportFailed: "Failed to export data",
		deleteAccount: "Delete account",
		deleteAccountWarning:
			"After you submit this request, your account will be scheduled for removal. Download anything you want to keep first.",
		confirmDelete: "Confirm deletion",
		deleteScheduled: "Account will be removed on {{date}}",
		deleteFailed: "Failed to request account deletion",
	},
	gdpr: {
		accountDeletionTitle: "Request account deletion",
		accountDeletionSubtitle: "Submit a request to delete your Muhaha account and associated data.",
		accountDeletionIntro:
			"Submitting this request immediately disables your account and signs you out. Download anything you want to keep first; your account and associated data will be permanently removed on the purge date.",
		requestDeletion: "Request account deletion",
		requestDeletionHint: "This disables your account, and a submitted deletion request cannot be undone.",
		deletionRequested: "Account will be removed on {{date}}",
		requestFailed: "Failed to request account deletion",
	},
	cosmetics: {
		title: "Avatars and cosmetics",
		subtitle: "Manage earned avatars, frames, and account entitlements",
		preview: "Current appearance",
		clearFrame: "Remove frame",
		frames: "Avatar frames",
		avatars: "Avatars",
		entitlements: "Account entitlements",
		permanent: "Permanent",
		equip: "Equip",
		frameEquipped: "Avatar frame updated",
		avatarEquipped: "Avatar updated",
		equipFailed: "Failed to apply the cosmetic",
		loadFailed: "Failed to load cosmetics",
		empty: "No cosmetics yet",
		emptyDesc: "Earned avatars and frames will appear here.",
	},
	wallet: {
		title: "Credits",
		subtitle: "Credits power AI generations and can be earned through activity",
		creditsBalance: "Credits",
		freeRemaining: "{{count}} free generations left",
		historyTitle: "Credits history",
		noTransactions: "No Credits activity yet",
		loadFailed: "Failed to load Credits",
		mubuxBalance: "Mubux",
		storeTitle: "Buy credits",
		storeNote: "* Paid with Mubux — denominations and prices come from the live store.",
		bestValue: "Best value",
		needsTopUp: "Not enough Mubux — top up in the Muhaha app first.",
		needsTopUpWeb: "Not enough Mubux — top up below first.",
		packBought: "Added {{amount}} credits",
		packPending: "Purchase confirmed — your credits will arrive shortly.",
		buyFailed: "Purchase failed",
		topupTitle: "Top up Mubux",
		topupNote: "* Secure payment via Stripe — Mubux arrives automatically after checkout.",
		topupSuccess: "Added {{amount}} Mubux",
		topupProcessing: "Payment received — your Mubux is on the way.",
		topupChecking: "Confirming your payment…",
		topupFailed: "Payment was not completed",
		mubuxHistory: "Mubux history",
		noMubuxTransactions: "No Mubux transactions yet",
		mubuxActivityLoadFailed: "Failed to load Mubux transactions",
		mubuxReasons: {
			recharge: "Mubux top-up",
			received: "Mubux received",
			spent: "Mubux spent",
		},
		reasons: {
			creditsRedeem: "Credits added",
			generation: "AI generation",
			generationRefund: "Generation refund",
			generationFree: "Free generation",
			generationFreeRefund: "Free slot restored",
		},
	},
		play: {
			reload: "Reload",
			fullscreen: "Fullscreen",
			fullscreenFailed: "Fullscreen is not available in this browser",
		loading: "Loading…",
		cannotLoad: "Failed to load",
		noBundleUrl: "This playable has no bundleUrl",
		bundleLoadFailed: "Failed to load the bundle: HTTP {{status}}",
		playableLoadFailed: "Failed to load the playable",
		expiredTitle: "This playable has ended",
		expiredDesc: "The author gave it a time limit, and that window has closed.",
		notFoundTitle: "This playable can't be opened",
		notFoundDesc: "It may have been deleted, or the author has not opened it up to you.",
		notFoundSignIn: "Sign in and try again",
		ownerUnpublishedNotice: "You have unpublished changes — publish the latest version in Muhaha Create.",
		sharePrivateHint: "This playable is visible only to you, so nobody else can open this link.",
		shareScopeHint: "This playable is limited to {{scope}} — anyone outside that can't open the link.",
		openInApp: "Open in app",
		getApp: "Get app",
		prevPlayable: "Previous",
		nextPlayable: "Next",
		creator: "Creator",
		untitledPlayable: "Untitled playable",
		publishedAgo: "Published {{time}}",
		playsLabel: "Plays",
		likesLabel: "Likes",
		commentsLabel: "Comments",
		favoritesLabel: "Saves",
		like: "Like",
		liked: "Liked",
		likeFailed: "Failed to update the like",
		favorite: "Save",
		favorited: "Saved",
		unfavorited: "Removed from saves",
		favoriteFailed: "Failed to update the save",
		remix: "Remix",
		share: "Share",
		linkCopied: "Link copied",
		shareTitle: "Share this game",
		sharedBy: "{{name}} shared this with you",
		shareLink: "Share link",
		copyLink: "Copy link",
		copyFailed: "Failed to copy",
		qrAlt: "Game share QR code",
		qrFailed: "Failed to generate QR code",
		commentsTitle: "Comments",
		commentsLoadFailed: "Failed to load comments",
		commentPlaceholder: "Share your thoughts…",
		commentFailed: "Failed to post the comment",
		noComments: "No comments yet — be the first!",
		deletedComment: "This comment was deleted",
		purchaseTitle: "Confirm purchase",
		purchasePrice: "{{amount}} Mubux",
		purchaseBalance: "Your balance: {{amount}} Mubux",
		purchaseInsufficient: "Not enough Mubux — top up in your wallet first.",
		purchaseConfirm: "Buy",
		shareToChat: "Share to chat",
		sharedToChat: "Shared to chat",
		shareVia: "Share via…",
	},
	rewards: {
		title: "Tasks and rewards",
		claim: "Claim",
		claimAll: "Claim all",
		claimed: "Claimed {{count}} Credits",
		claimFailed: "Failed to claim the reward",
		loadFailed: "Failed to load rewards",
		inviteTitle: "Invite friends",
		inviteCount: "{{count}} invited",
		inviteCopied: "Invite link copied",
		inviteMessage: "Friends who sign up with your link earn you both {{count}} Coins.",
		inviteMessageBare: "Invite friends to play and create with you on Muhaha.",
		copyInvite: "Copy invite link",
		shareInvite: "Share",
	},
	invite: {
		title: "Invite",
		readyTitle: "Your invite is ready",
		readyDesc:
			"Sign up and this invite code is applied automatically — you and your friend both get the reward.",
		signUpCta: "Sign up or sign in",
		memberTitle: "You're already signed in",
		memberDesc: "Invites apply to new sign-ups only — go explore instead.",
		invalidTitle: "This invite link isn't valid",
		invalidDesc: "The link may be expired or incomplete. You can still join Muhaha.",
		browseCta: "Browse Muhaha",
	},
	report: {
		title: "Report",
		description: "Tell us what's wrong and we'll review it.",
		submitted: "Report submitted",
		submitFailed: "Couldn't submit the report",
		type: {
			spam: "Spam",
			nuditySexualContent: "Nudity & sexual content",
			violentSpeech: "Violent speech",
			suicideSelfHarm: "Suicide or self-harm",
			childSafety: "Child safety",
			illegalRegulatedBehaviors: "Illegal & regulated behaviors",
			misinformation: "Misinformation",
			terrorismHateHarassment: "Terrorism, hate & harassment",
		},
	},
} satisfies typeof zh;
